import { ObservableValue } from "./observable-value.js";
/**
 * Options object for tracing method calls
 */

/**
 * Helper class that can be used to trace method calls programmatically
 *
 * Usage example:
 * ```ts
 * const methodTracer: Disposable = Trace.method({
 *     object: myObjectInstance,           // You can define an object constructor for static methods as well
 *     method: myObjectInstance.method,    // The method to be tracked
 *     methodName: 'method', // Unique identifier for the method
 *     isAsync: true,                      // if you set to async, method finished will be *await*-ed
 *     onCalled: (traceData) => {
 *         console.log("Method called:", traceData)
 *     },
 *     onFinished: (traceData) => {
 *         console.log("Method call finished:", traceData)
 *     },
 *     onError: (traceData) => {
 *         console.log("Method throwed an error:", traceData)
 *     }
 * });
 * ```
 */
export class Trace {
  static getMethodTrace(object, methodName) {
    const objectTrace = this.objectTraces.get(object);
    return objectTrace.methodMappings.get(methodName);
  }

  static traceStart(methodTrace, args) {
    const startDateTime = new Date();
    const traceValue = {
      methodArguments: args,
      startDateTime
    };
    methodTrace.callObservable.setValue(traceValue);
    return traceValue;
  }

  static traceFinished(methodTrace, args, callTrace, returned) {
    const finishedTrace = {
      methodArguments: args,
      startDateTime: callTrace.startDateTime,
      finishedDateTime: new Date(),
      returned
    };
    methodTrace.finishedObservable.setValue(finishedTrace);
  }

  static traceError(methodTrace, args, callTrace, error) {
    const errorTrace = {
      methodArguments: args,
      startDateTime: callTrace.startDateTime,
      errorDateTime: new Date(),
      error
    };
    methodTrace.errorObservable.setValue(errorTrace);
    return errorTrace;
  }

  static callMethod(object, methodName, args) {
    const methodTrace = this.getMethodTrace(object, methodName);
    const start = this.traceStart(methodTrace, args);

    try {
      const returned = methodTrace.originalMethod.call(object, ...args);
      this.traceFinished(methodTrace, args, start, returned);
      return returned;
    } catch (error) {
      this.traceError(methodTrace, args, start, error);
      throw error;
    }
  }

  static async callMethodAsync(object, methodName, args) {
    const methodTrace = this.getMethodTrace(object, methodName);
    const start = this.traceStart(methodTrace, args);

    try {
      const returned = await methodTrace.originalMethod.call(object, ...args);
      this.traceFinished(methodTrace, args, start, returned);
      return returned;
    } catch (error) {
      this.traceError(methodTrace, args, start, error);
      throw error;
    }
  }
  /**
   * Creates an observer that will be observe method calls, finishes and errors
   * @param options The options object for the trace
   */


  static method(options) {
    var _this = this;

    // add object mapping
    if (!this.objectTraces.has(options.object)) {
      this.objectTraces.set(options.object, {
        methodMappings: new Map()
      });
    } // setup override if needed


    if (!options.object[options.methodName].isTraced) {
      const overriddenMethod = options.isAsync ? function () {
        for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
          args[_key] = arguments[_key];
        }

        return _this.callMethodAsync(options.object, options.methodName, args);
      } : function () {
        for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
          args[_key2] = arguments[_key2];
        }

        return _this.callMethod(options.object, options.methodName, args);
      };
      Object.defineProperty(overriddenMethod, 'name', {
        value: options.methodName
      });
      Object.defineProperty(overriddenMethod, 'isTraced', {
        value: options.methodName
      });
      options.object[options.methodName] = overriddenMethod;
    }

    const objectTrace = this.objectTraces.get(options.object); // add method mapping if needed

    if (!objectTrace.methodMappings.has(options.methodName)) {
      objectTrace.methodMappings.set(options.methodName, {
        originalMethod: options.method,
        callObservable: new ObservableValue(),
        finishedObservable: new ObservableValue(),
        errorObservable: new ObservableValue()
      });
    }

    const methodTrace = objectTrace.methodMappings.get(options.methodName);
    const callbacks = [options.onCalled && methodTrace.callObservable.subscribe(options.onCalled), options.onFinished && methodTrace.finishedObservable.subscribe(options.onFinished), options.onError && methodTrace.errorObservable.subscribe(options.onError)]; // Subscribe and return the observer

    return {
      dispose: () => callbacks.forEach(c => c && c.dispose())
    };
  }

}
Trace.objectTraces = new Map();
//# sourceMappingURL=trace.js.map